import { AccessDeniedPage, GenericErrorPage, NotFoundPage } from "@climatepartner/components/ErrorPages";
import { Spinner } from "@climatepartner/components/Spinner";
import { ReactElement, useContext } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";

import { getCustomerLandingPath } from "../../routing/paths/customer";
import { OrganisationUnusableErrorPage } from "../../ui/errors/organisation-unusable-error-page";
import { HttpException, useOrganisation } from "../use-organisation";
import { useSubAccountIds, useSubAccountTree } from "../use-sub-account-tree";
import { UserContext } from "../user-context";
import { isEmployeeInternal } from "../user-utils";

type Props = {
  children: ReactElement | ReactElement[];
};

const renderOrganisationError = (error: HttpException): ReactElement => {
  switch (error.statusCode) {
    case 404:
      return <NotFoundPage />;
    case 400:
      return <OrganisationUnusableErrorPage />;
    default:
      return <GenericErrorPage />;
  }
};

/**
 * A customer outside their own account and its sub-accounts is sent to their home page, whatever
 * the organisation turned out to be (staff may open any account).
 */
export const OrganisationAccessGuard = ({ children }: Props): ReactElement => {
  const { customerId } = useParams();
  const { user, hasPermission } = useContext(UserContext);
  const { search } = useLocation();

  const { isLoading: isOrganisationLoading, isError, error } = useOrganisation(customerId);
  const { isLoading: isSubAccountTreeLoading } = useSubAccountTree({ isEnabled: true });
  const subAccountIds = useSubAccountIds({ isEnabled: true });

  if (isOrganisationLoading || isSubAccountTreeLoading) {
    return <Spinner />;
  }

  const hasAccess =
    isEmployeeInternal(hasPermission) ||
    (customerId !== undefined && (customerId === user?.customerId || subAccountIds.includes(customerId)));

  if (!hasAccess) {
    return user?.customerId ? (
      <Navigate to={{ pathname: getCustomerLandingPath(user.customerId), search }} replace />
    ) : (
      <AccessDeniedPage />
    );
  }

  if (isError && error) {
    return renderOrganisationError(error);
  }

  return <>{children}</>;
};
